import React, { useState, useEffect } from "react";
import styled, { keyframes } from "styled-components";
import * as LucideIcons from "lucide-react";
import Cookies from "js-cookie";
import { useWebsiteConfigQuery } from "hooks/useWebsiteConfig";
import storage from "utils/Storage";
import { API } from "utils/Helper";

const getUserId = () => {
  let uid = Cookies.get("uid");
  if (!uid) {
    try {
      const token = storage.getKey("token");
      if (token) {
        const payload = JSON.parse(atob(token.split(".")[1]));
        uid = payload.id || payload.uid;
      }
    } catch (e) {}
  }
  return uid ? Number(uid) : null;
};

const getIconComponent = (iconName) => {
  if (iconName === 'InfinityIcon') return LucideIcons.Infinity;
  const IconComp = LucideIcons[iconName];
  return IconComp || LucideIcons.HelpCircle;
};

// ─── Skeleton shimmer ───────────────────────────────────────────────────────
const shimmer = keyframes`
  0%   { background-position: -600px 0; }
  100% { background-position:  600px 0; }
`;

const Skeleton = styled.div`
  border-radius: ${({ radius }) => radius || "6px"};
  height: ${({ h }) => h || "14px"};
  width: ${({ w }) => w || "100%"};
  background: linear-gradient(90deg, #2a2e30 25%, #343a3c 50%, #2a2e30 75%);
  background-size: 600px 100%;
  animation: ${shimmer} 1.4s infinite linear;
`;

// ─── Styled components ───────────────────────────────────────────────────────
const Container = styled.div`
  color: #f5f6f7;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const TopDashboardRow = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  margin-bottom: 30px;
  @media (max-width: 992px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const TopStatCard = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  transition: border-color 0.2s;
  &:hover { border-color: #1fc16b; }
  .label {
    font-size: 11px; font-weight: 700; color: #b1b6bb;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
  .value {
    font-size: 24px; font-weight: 900;
    color: ${props => props.color || "#ffffff"};
    margin-top: 8px;
  }
`;

const InfoCol = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 30px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  h2 { font-size: 24px; font-weight: 800; color: #ffffff; margin: 0 0 12px 0; }
  p  { color: #b1b6bb; font-size: 14px; line-height: 1.6; margin-bottom: 25px; }
`;

const MetricsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  margin-bottom: 30px;
  max-width: 576px;
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const Metric = styled.div`
  .num   { font-size: 28px; font-weight: 800; color: #ffffff; letter-spacing: -0.5px; }
  .label { font-size: 13px; color: #b1b6bb; margin-top: 4px; line-height: 1.4; }
`;

const SectionTitle = styled.h3`
  font-size: 20px; font-weight: 800; color: #ffffff;
  margin: 30px 0 20px 0; text-transform: uppercase; letter-spacing: 0.5px;
`;

const AdvantagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-bottom: 40px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const AdvCard = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  align-items: start;
  gap: 15px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
  transition: border-color 0.2s;
  &:hover { border-color: #1fc16b; }
`;

const IconWrap = styled.div`
  background: rgba(31,193,107,0.1);
  color: #1fc16b;
  width: 44px; height: 44px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  border: 1px solid rgba(31,193,107,0.15);
`;

const AdvInfo = styled.div`
  h4 { font-size: 15px; font-weight: bold; color: #ffffff; margin: 0; }
  p  { color: #b1b6bb; font-size: 13px; margin-top: 6px; line-height: 1.5; }
`;

const RulesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 20px;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const RuleCard = styled.div`
  background: #292d2e;
  border: 1px solid #3a4142;
  border-radius: 8px;
  padding: 20px;
  .header {
    display: flex; align-items: center; gap: 8px;
    font-weight: 800; color: #ffffff; font-size: 14px;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
  .desc    { color: #b1b6bb; font-size: 13px; margin-top: 8px; line-height: 1.5; }
  .formula {
    background: #1a1d1e; border-radius: 6px; padding: 10px 12px;
    font-family: monospace; font-size: 12px; color: #1fc16b;
    margin-top: 15px; border: 1px solid #3a4142; word-break: break-all;
  }
`;

// ─── Skeleton sub-components ─────────────────────────────────────────────────
const SkeletonAdvCard = () => (
  <AdvCard>
    <div style={{ flexShrink: 0 }}>
      <Skeleton w="44px" h="44px" radius="8px" />
    </div>
    <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
      <Skeleton w="60%" h="14px" />
      <Skeleton w="90%" h="11px" />
      <Skeleton w="75%" h="11px" />
    </div>
  </AdvCard>
);

const SkeletonRuleCard = () => (
  <RuleCard>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
      <Skeleton w="20px" h="20px" radius="4px" />
      <Skeleton w="50%" h="13px" />
    </div>
    <Skeleton w="100%" h="11px" />
    <Skeleton w="80%"  h="11px" style={{ marginTop: 4 }} />
    <div style={{ marginTop: 15, background: "#1a1d1e", borderRadius: 6, padding: "10px 12px", border: "1px solid #3a4142" }}>
      <Skeleton w="85%" h="11px" />
    </div>
  </RuleCard>
);

// ─── Component ───────────────────────────────────────────────────────────────
const Overview = () => {
  const isLogged = !!storage.getKey('logged');
  const { data: config, isLoading } = useWebsiteConfigQuery();
  const [stats, setStats] = useState({
    totalReferrals: 0,
    monthlyFtd: 0,
    totalDeposits: '0.00',
    totalCommission: '0.00'
  });
  const uid = getUserId();

  useEffect(() => {
    if (!uid) return;
    fetch(`${API}/api/affiliate/overview/${uid}`)
      .then(res => res.json())
      .then(resData => {
        if (resData.success && resData.data) {
          setStats(resData.data);
        }
      })
      .catch(err => console.error("Error fetching overview stats:", err));
  }, [uid]);

  const overviewData       = config?.affiliate_overview   || {};
  const metrics            = overviewData.metrics          || [];
  const advantages         = overviewData.advantages       || [];
  const commissionRules    = overviewData.commissionRules  || [];

  return (
    <Container>
      {/* ── Logged-in quick stats ── */}
      {isLogged && (
        <TopDashboardRow>
          <TopStatCard>
            <div className="label">Total Referrals</div>
            <div className="value">{stats.totalReferrals}</div>
          </TopStatCard>
          <TopStatCard>
            <div className="label">Monthly FTD</div>
            <div className="value">{stats.monthlyFtd}</div>
          </TopStatCard>
          <TopStatCard>
            <div className="label">Total Deposits</div>
            <div className="value">${parseFloat(stats.totalDeposits || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
          </TopStatCard>
          <TopStatCard>
            <div className="label">Commission (USD)</div>
            <div className="value" style={{ color: "#1fc16b" }}>${parseFloat(stats.totalCommission || 0).toFixed(2)}</div>
          </TopStatCard>
        </TopDashboardRow>
      )}

      {/* ── Overview info + metrics ── */}
      <InfoCol>
        <h2>Overview</h2>
        <p>Earn commission for all bets placed by your referrals across Casino and Sportsbook.</p>

        <MetricsRow>
          {isLoading
            ? [0, 1, 2].map(i => (
                <Metric key={i}>
                  <Skeleton w="70%" h="28px" radius="6px" />
                  <Skeleton w="55%" h="13px" radius="4px" style={{ marginTop: 6 }} />
                </Metric>
              ))
            : metrics.map((m, i) => (
                <Metric key={i}>
                  <div className="num">{m.num}</div>
                  <div className="label" dangerouslySetInnerHTML={{ __html: m.label.replace(/\n/g, '<br />') }} />
                </Metric>
              ))
          }
        </MetricsRow>

        <p style={{ color: "#b1b6bb", marginBottom: 0 }}>
          Share your referral links from the Campaigns tab to start earning lifetime commission.
        </p>
      </InfoCol>

      {/* ── Exclusive Advantages ── */}
      <SectionTitle>Exclusive Advantages</SectionTitle>
      <AdvantagesGrid>
        {isLoading
          ? [0, 1, 2, 3, 4, 5].map(i => <SkeletonAdvCard key={i} />)
          : advantages.map((a, i) => {
              const IconComponent = getIconComponent(a.icon);
              return (
                <AdvCard key={i}>
                  <IconWrap><IconComponent size={20} /></IconWrap>
                  <AdvInfo>
                    <h4>{a.title}</h4>
                    <p>{a.desc}</p>
                  </AdvInfo>
                </AdvCard>
              );
            })
        }
      </AdvantagesGrid>

      {/* ── Commission Rules ── */}
      <SectionTitle>Commission Rules</SectionTitle>
      <p style={{ color: "#b1b6bb", fontSize: "14px", marginTop: "-10px", marginBottom: "20px" }}>
        Our default commission rate is 10% but you can calculate specific rates for our products using the formulas below.
      </p>
      <RulesGrid>
        {isLoading
          ? [0, 1, 2].map(i => <SkeletonRuleCard key={i} />)
          : commissionRules.map((rule, i) => {
              const RuleIcon = LucideIcons[rule.icon] || LucideIcons.HelpCircle;
              return (
                <RuleCard key={i}>
                  <div className="header"><RuleIcon size={16} style={{ color: "#1fc16b" }} /> {rule.title}</div>
                  <div className="desc">{rule.desc}</div>
                  <div className="formula">{rule.formula}</div>
                </RuleCard>
              );
            })
        }
      </RulesGrid>
    </Container>
  );
};

export default Overview;
